import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { DEMOS } from '../demos/demos';
import { DEMO_CATEGORIES, DemoCategory, DemoEntry } from '../demos/demos.model';
import { ANGULAR_MAJOR, GITHUB_URL, INSTALL_CMD } from '../version';

/** Индекс примеров: hero-витрина + каталог (поиск, чипы категорий, секции карточек). */
@Component({
  selector: 'app-demo-index',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './demo-index.component.html',
  styleUrls: ['./demo-index.component.scss'],
})
export class DemoIndexComponent implements OnDestroy {
  readonly categories = DEMO_CATEGORIES;
  readonly major = ANGULAR_MAJOR;
  readonly githubUrl = GITHUB_URL;
  readonly installCmd = INSTALL_CMD;

  demos: DemoEntry[] = DEMOS;
  query = '';
  selected: DemoCategory | 'All' = 'All';
  copied = false;

  // Хендл таймера сброса «✓ copied» — чистится в ngOnDestroy, чтобы не писать
  // в уничтоженный компонент при уходе со страницы в течение 1.5с окна.
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  /** Секции, видимые под текущим чипом. */
  get shownCategories(): DemoCategory[] {
    return this.selected === 'All' ? this.categories : [this.selected];
  }

  /** Есть ли хоть одно совпадение под чипом и поиском. */
  get anyVisible(): boolean {
    return this.shownCategories.some(c => this.visible(c).length > 0);
  }

  count(cat: DemoCategory): number {
    return this.demos.filter(d => d.category === cat).length;
  }

  visible(cat: DemoCategory): DemoEntry[] {
    const q = this.query.trim().toLowerCase();
    return this.demos.filter(d =>
      d.category === cat &&
      (!q || d.title.toLowerCase().includes(q) || d.description.toLowerCase().includes(q)));
  }

  select(cat: DemoCategory | 'All'): void {
    this.selected = cat;
  }

  copyInstall(): void {
    const done = () => {
      this.copied = true;
      if (this.copiedTimer !== null) {
        clearTimeout(this.copiedTimer);
      }
      this.copiedTimer = setTimeout(() => {
        this.copied = false;
        this.copiedTimer = null;
      }, 1500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(this.installCmd).then(done, () => this.copyFallback(done));
    } else {
      this.copyFallback(done);
    }
  }

  scrollToCatalog(): void {
    document.getElementById('demo-catalog')?.scrollIntoView({ behavior: 'smooth' });
  }

  ngOnDestroy(): void {
    if (this.copiedTimer !== null) {
      clearTimeout(this.copiedTimer);
    }
  }

  // Fallback для окружений без Clipboard API (небезопасный контекст и т.п.).
  private copyFallback(done: () => void): void {
    const ta = document.createElement('textarea');
    ta.value = this.installCmd;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      // execCommand возвращает false, если браузер отказал в копировании —
      // в этом случае UI не должен показывать «✓ copied». Может и бросить
      // в отдельных окружениях — ловим, чтобы не улетело unhandled rejection.
      if (document.execCommand('copy')) {
        done();
      }
    } catch {
      // копирование не удалось — просто не вызываем done()
    } finally {
      ta.remove();
    }
  }
}
